import React, { useState } from "react";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { YinYangPanel } from "./YinYangScale";
import type { AnalysisResult } from "../api/mockData";

interface ResultScreenProps {
  result: AnalysisResult;
  onReset: () => void;
}

type Tab = "overview" | "features" | "clothing";

const familyColors: Record<string, { bg: string; text: string; accent: string }> = {
  Dramatic: { bg: "var(--rose-mist)", text: "var(--rose)", accent: "var(--rose-light)" },
  Natural: { bg: "var(--sage-light)", text: "var(--sage)", accent: "var(--sage-light)" },
  Classic: { bg: "var(--gold-light)", text: "var(--gold)", accent: "var(--gold-light)" },
  Gamine: { bg: "var(--mauve-light)", text: "var(--mauve)", accent: "var(--mauve-light)" },
  Romantic: { bg: "var(--rose-mist)", text: "var(--rose)", accent: "var(--rose-light)" },
};

export function ResultScreen({ result, onReset }: ResultScreenProps) {
  const [activeTab, setActiveTab] = useState<Tab>("overview");
  const [copied, setCopied] = useState(false);
  const [heroIndex, setHeroIndex] = useState(0);
  const { kibbeType, confidence, detectedFeatures, whyThisType, genderRu, yinYangBalance, photoDataUrls } = result;
  const colors = familyColors[kibbeType.family] || familyColors.Dramatic;
  const photoUrl = photoDataUrls[heroIndex] || photoDataUrls[0] || "";

  const shareUrl = `${window.location.origin}${window.location.pathname}#/result/${result.id}`;

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: `Kibbe: ${kibbeType.nameRu}`, text: `Мой типаж по Кибби: ${kibbeType.nameRu} (${kibbeType.name})`, url: shareUrl });
      } catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const tabs: { key: Tab; label: string }[] = [
    { key: "overview", label: "Типаж" },
    { key: "features", label: "Черты" },
    { key: "clothing", label: "Одежда" },
  ];

  return (
    <div className="min-h-[100dvh] pb-8 relative">
      {/* Hero section */}
      <div className="relative h-[380px] overflow-hidden animate-fade-in">
        <img
          src={photoUrl}
          alt="Ваше фото"
          className="w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(180deg, oklch(0.15 0.01 30 / 0.1) 0%, oklch(0.15 0.01 30 / 0.7) 70%, var(--background) 100%)",
          }}
        />

        {/* Back button */}
        <button
          onClick={onReset}
          className="absolute top-12 left-5 w-10 h-10 rounded-full flex items-center justify-center transition-transform active:scale-90"
          style={{ background: "oklch(1 0 0 / 0.2)", backdropFilter: "blur(10px)" }}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {/* Share button */}
        <button
          onClick={handleShare}
          className="absolute top-12 right-5 w-10 h-10 rounded-full flex items-center justify-center transition-transform active:scale-90"
          style={{ background: "oklch(1 0 0 / 0.2)", backdropFilter: "blur(10px)" }}
        >
          {copied ? (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M5 10.5L8.5 14L15 6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M5 12.5V14.5C5 15.052 5.448 15.5 6 15.5H14C14.552 15.5 15 15.052 15 14.5V12.5M10 4.5V11.5M10 4.5L7 7.5M10 4.5L13 7.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </button>

        {/* Photo thumbnails (when multiple photos) */}
        {photoDataUrls.length > 1 && (
          <div
            className="absolute top-12 left-1/2 -translate-x-1/2 flex gap-1.5 px-3 py-2 rounded-full"
            style={{ background: "oklch(0 0 0 / 0.35)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)" }}
          >
            {photoDataUrls.map((_, i) => (
              <button
                key={i}
                onClick={() => setHeroIndex(i)}
                className="w-2 h-2 rounded-full transition-all duration-300"
                style={{
                  background: heroIndex === i ? "white" : "oklch(1 0 0 / 0.4)",
                  transform: heroIndex === i ? "scale(1.3)" : "scale(1)",
                }}
              />
            ))}
          </div>
        )}

        {/* Type name overlay */}
        <div className="absolute bottom-6 left-0 right-0 px-6">
          <h1
            className="font-editorial text-[40px] leading-[1.05] tracking-[-0.02em] text-white animate-fade-up delay-200"
            style={{ fontWeight: 300 }}
          >
            {kibbeType.nameRu}
          </h1>
          <div className="animate-fade-up delay-300 mt-2">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full"
              style={{ background: "oklch(1 0 0 / 0.15)", backdropFilter: "blur(8px)" }}
            >
              <div className="w-2 h-2 rounded-full" style={{ background: colors.text }} />
              <span className="font-body text-[12px] font-medium text-white uppercase tracking-[0.06em]">
                {genderRu} • {kibbeType.family} • {confidence}%
              </span>
            </div>
          </div>
          <p
            className="font-body text-[13px] text-white/70 mt-2 animate-fade-up delay-400"
          >
            {kibbeType.name}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-5 -mt-1">
        <div
          className="flex gap-1 p-1 rounded-[14px] animate-fade-up delay-500"
          style={{ background: "var(--muted)" }}
        >
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className="flex-1 py-2.5 rounded-[10px] font-body text-[13px] font-medium transition-all duration-300"
              style={{
                background: activeTab === tab.key ? "var(--card)" : "transparent",
                color: activeTab === tab.key ? "var(--foreground)" : "var(--muted-foreground)",
                boxShadow: activeTab === tab.key ? "0 1px 3px oklch(0 0 0 / 0.06)" : "none",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="px-5 mt-5">
        {activeTab === "overview" && (
          <OverviewTab
            kibbeType={kibbeType}
            whyThisType={whyThisType}
            colors={colors}
          />
        )}
        {activeTab === "features" && (
          <FeaturesTab
            detectedFeatures={detectedFeatures}
            kibbeType={kibbeType}
            colors={colors}
            yinYangBalance={yinYangBalance}
          />
        )}
        {activeTab === "clothing" && (
          <ClothingTab
            kibbeType={kibbeType}
            colors={colors}
          />
        )}
      </div>

      {/* Bottom CTA */}
      <div className="px-5 mt-8 animate-fade-up delay-700">
        <Button
          onClick={onReset}
          className="w-full h-[54px] rounded-[16px] text-[15px] font-medium font-body transition-all duration-300 hover:scale-[1.01] active:scale-[0.98]"
          style={{
            background: "linear-gradient(145deg, var(--rose) 0%, var(--mauve) 100%)",
            color: "white",
            boxShadow: "0 8px 32px oklch(0.48 0.14 12 / 0.25)",
            border: "none",
          }}
        >
          Проанализировать другое фото
        </Button>
      </div>
    </div>
  );
}

/* ===== Overview Tab ===== */

function OverviewTab({
  kibbeType,
  whyThisType,
  colors,
}: {
  kibbeType: AnalysisResult["kibbeType"];
  whyThisType: string[];
  colors: { bg: string; text: string; accent: string };
}) {
  return (
    <div className="space-y-4">
      {/* Dominant line */}
      <Card
        className="p-5 rounded-[18px] border-0 animate-fade-up delay-100"
        style={{ background: colors.bg }}
      >
        <div className="flex items-start gap-3">
          <div
            className="w-10 h-10 rounded-[12px] flex items-center justify-center shrink-0 mt-0.5"
            style={{ background: colors.accent }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M3 17L10 3L17 17" stroke={colors.text} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div>
            <div
              className="font-body text-[11px] uppercase tracking-[0.06em] mb-1"
              style={{ color: colors.text }}
            >
              Доминантная линия
            </div>
            <div className="font-body text-[15px] font-medium" style={{ color: "var(--foreground)" }}>
              {kibbeType.dominantLine}
            </div>
          </div>
        </div>
      </Card>

      {/* Description */}
      <Card className="p-5 rounded-[18px] border-0 animate-fade-up delay-200" style={{ background: "var(--card)" }}>
        <p className="font-body text-[14px] leading-[1.65]" style={{ color: "var(--foreground)" }}>
          {kibbeType.fullDescription}
        </p>
      </Card>

      {/* Why this type */}
      <div className="animate-fade-up delay-300">
        <h3
          className="font-editorial text-[20px] mb-3 px-1"
          style={{ fontWeight: 500, color: "var(--foreground)" }}
        >
          Почему этот типаж?
        </h3>
        <div className="space-y-2.5">
          {whyThisType.map((reason, i) => (
            <div
              key={i}
              className="flex gap-3 items-start animate-slide-in"
              style={{ animationDelay: `${400 + i * 100}ms` }}
            >
              <div
                className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 font-body text-[11px] font-semibold"
                style={{ background: colors.bg, color: colors.text }}
              >
                {i + 1}
              </div>
              <p className="font-body text-[13px] leading-[1.55]" style={{ color: "var(--secondary-foreground)" }}>
                {reason}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Essence keywords */}
      <Card className="p-5 rounded-[18px] border-0 animate-fade-up delay-500" style={{ background: "var(--card)" }}>
        <div
          className="font-body text-[11px] uppercase tracking-[0.06em] mb-3"
          style={{ color: "var(--muted-foreground)" }}
        >
          Ключевые слова
        </div>
        <div className="flex flex-wrap gap-2">
          {kibbeType.essenceKeywords.map((keyword, i) => (
            <span
              key={i}
              className="px-3.5 py-1.5 rounded-full font-body text-[13px] font-medium"
              style={{ background: colors.bg, color: colors.text }}
            >
              {keyword}
            </span>
          ))}
        </div>
      </Card>

      {/* Celebrities */}
      <Card className="p-5 rounded-[18px] border-0 animate-fade-up delay-600" style={{ background: "var(--card)" }}>
        <div
          className="font-body text-[11px] uppercase tracking-[0.06em] mb-3"
          style={{ color: "var(--muted-foreground)" }}
        >
          Известные представители
        </div>
        <div className="space-y-2">
          {kibbeType.celebrities.map((celeb, i) => (
            <div key={i} className="flex items-center gap-3">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center font-editorial text-[14px]"
                style={{ background: colors.bg, color: colors.text, fontWeight: 600 }}
              >
                {celeb[0]}
              </div>
              <span className="font-body text-[14px]" style={{ color: "var(--foreground)" }}>
                {celeb}
              </span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

/* ===== Features Tab ===== */

function FeaturesTab({
  detectedFeatures,
  kibbeType,
  colors,
  yinYangBalance,
}: {
  detectedFeatures: AnalysisResult["detectedFeatures"];
  kibbeType: AnalysisResult["kibbeType"];
  colors: { bg: string; text: string; accent: string };
  yinYangBalance: AnalysisResult["yinYangBalance"];
}) {
  return (
    <div className="space-y-4">
      {/* Yin-Yang Balance */}
      <Card className="p-5 rounded-[18px] border-0 animate-fade-up" style={{ background: "var(--card)" }}>
        <h3
          className="font-editorial text-[18px] mb-4"
          style={{ fontWeight: 500, color: "var(--foreground)" }}
        >
          Баланс Инь–Ян
        </h3>
        <YinYangPanel balance={yinYangBalance} />
      </Card>

      {/* Detected features */}
      <div className="animate-fade-up delay-100">
        <h3
          className="font-editorial text-[20px] mb-3 px-1"
          style={{ fontWeight: 500, color: "var(--foreground)" }}
        >
          Обнаруженные черты
        </h3>
        <div className="space-y-2.5">
          {detectedFeatures.map((feat, i) => (
            <Card
              key={i}
              className="p-4 rounded-[14px] border-0 animate-slide-in flex items-center justify-between"
              style={{ background: "var(--card)", animationDelay: `${100 + i * 80}ms` }}
            >
              <div>
                <div
                  className="font-body text-[11px] uppercase tracking-[0.04em]"
                  style={{ color: "var(--muted-foreground)" }}
                >
                  {feat.category} — {feat.feature}
                </div>
                <div className="font-body text-[14px] font-medium mt-0.5" style={{ color: "var(--foreground)" }}>
                  {feat.value}
                </div>
              </div>
              <div
                className="w-8 h-8 rounded-[10px] flex items-center justify-center shrink-0"
                style={{ background: colors.bg }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M5 7L6.5 8.5L9 5.5" stroke={colors.text} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Body characteristics */}
      <div className="animate-fade-up delay-300">
        <h3
          className="font-editorial text-[20px] mb-3 px-1"
          style={{ fontWeight: 500, color: "var(--foreground)" }}
        >
          Характеристики фигуры
        </h3>
        <Card className="p-5 rounded-[18px] border-0" style={{ background: "var(--card)" }}>
          <ul className="space-y-3">
            {kibbeType.bodyCharacteristics.map((char, i) => (
              <li key={i} className="flex items-start gap-3">
                <div
                  className="w-1.5 h-1.5 rounded-full shrink-0 mt-[7px]"
                  style={{ background: colors.text }}
                />
                <span className="font-body text-[14px] leading-[1.5]" style={{ color: "var(--foreground)" }}>
                  {char}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      {/* Facial features */}
      <div className="animate-fade-up delay-400">
        <h3
          className="font-editorial text-[20px] mb-3 px-1"
          style={{ fontWeight: 500, color: "var(--foreground)" }}
        >
          Черты лица
        </h3>
        <Card className="p-5 rounded-[18px] border-0" style={{ background: "var(--card)" }}>
          <ul className="space-y-3">
            {kibbeType.facialFeatures.map((feat, i) => (
              <li key={i} className="flex items-start gap-3">
                <div
                  className="w-1.5 h-1.5 rounded-full shrink-0 mt-[7px]"
                  style={{ background: colors.text }}
                />
                <span className="font-body text-[14px] leading-[1.5]" style={{ color: "var(--foreground)" }}>
                  {feat}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}

/* ===== Clothing Tab ===== */

function ClothingTab({
  kibbeType,
  colors,
}: {
  kibbeType: AnalysisResult["kibbeType"];
  colors: { bg: string; text: string; accent: string };
}) {
  const recs = kibbeType.clothingRecommendations;

  const sections: { title: string; icon: React.ReactNode; items: string[] }[] = [
    {
      title: "Силуэты",
      icon: (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path d="M9 2L3 16H15L9 2Z" stroke={colors.text} strokeWidth="1.3" strokeLinejoin="round" />
        </svg>
      ),
      items: recs.silhouettes,
    },
    {
      title: "Ткани",
      icon: (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path d="M2 6C5 4 8 8 11 6C14 4 16 6 16 6V14C14 14 12 12 9 14C6 16 4 14 2 14V6Z" stroke={colors.text} strokeWidth="1.3" />
        </svg>
      ),
      items: recs.fabrics,
    },
    {
      title: "Принты и цвета",
      icon: (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <circle cx="7" cy="7" r="4" stroke={colors.text} strokeWidth="1.3" />
          <circle cx="11" cy="11" r="4" stroke={colors.text} strokeWidth="1.3" />
        </svg>
      ),
      items: recs.patterns,
    },
    {
      title: "Аксессуары",
      icon: (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <circle cx="9" cy="9" r="6" stroke={colors.text} strokeWidth="1.3" />
          <circle cx="9" cy="9" r="2" stroke={colors.text} strokeWidth="1.3" />
        </svg>
      ),
      items: recs.accessories,
    },
  ];

  return (
    <div className="space-y-4">
      {/* Recommendation cards */}
      {sections.map((section, si) => (
        <Card
          key={si}
          className="p-5 rounded-[18px] border-0 animate-fade-up"
          style={{ background: "var(--card)", animationDelay: `${si * 100}ms` }}
        >
          <div className="flex items-center gap-2.5 mb-3">
            <div
              className="w-8 h-8 rounded-[10px] flex items-center justify-center"
              style={{ background: colors.bg }}
            >
              {section.icon}
            </div>
            <h4 className="font-body text-[14px] font-semibold" style={{ color: "var(--foreground)" }}>
              {section.title}
            </h4>
          </div>
          <div className="flex flex-wrap gap-2">
            {section.items.map((item, i) => (
              <span
                key={i}
                className="px-3 py-1.5 rounded-[10px] font-body text-[13px]"
                style={{ background: "var(--muted)", color: "var(--secondary-foreground)" }}
              >
                {item}
              </span>
            ))}
          </div>
        </Card>
      ))}

      {/* What to avoid */}
      <div className="animate-fade-up delay-500">
        <h3
          className="font-editorial text-[20px] mb-3 px-1"
          style={{ fontWeight: 500, color: "var(--foreground)" }}
        >
          Лучше избегать
        </h3>
        <Card
          className="p-5 rounded-[18px] border-0"
          style={{ background: "oklch(0.97 0.01 25 / 0.5)" }}
        >
          <ul className="space-y-2.5">
            {recs.avoid.map((item, i) => (
              <li key={i} className="flex items-center gap-3">
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                  style={{ background: "oklch(0.92 0.02 25)" }}
                >
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M2.5 2.5L7.5 7.5M7.5 2.5L2.5 7.5" stroke="oklch(0.55 0.15 25)" strokeWidth="1.2" strokeLinecap="round" />
                  </svg>
                </div>
                <span className="font-body text-[14px]" style={{ color: "var(--secondary-foreground)" }}>
                  {item}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}
